import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useAuth } from '../auth/useAuth'
import { apiFetch } from '../lib/api'

export interface NotificationItem {
  id: string
  type: 'unreachable_connection' | 'fingerprint_changed'
  entityType: string
  entityId: string | null
  entityName: string
  state: string
  createdAt: string
}

interface NotificationsContextValue {
  notifications: NotificationItem[]
  hasActive: boolean
  refresh: () => void
  isLoading: boolean
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null)

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isGlobalAdmin } = useAuth()
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const refresh = useCallback(() => {
    if (!isAuthenticated || !isGlobalAdmin) return
    setIsLoading(true)
    apiFetch<{ items: NotificationItem[] }>('/api/v1/notifications')
      .then(data => setNotifications(data.items))
      .catch(() => { /* silent */ })
      .finally(() => setIsLoading(false))
  }, [isAuthenticated, isGlobalAdmin])

  useEffect(() => {
    refresh()
  }, [refresh])

  return (
    <NotificationsContext.Provider value={{
      notifications,
      hasActive: notifications.some(n => n.state === 'active'),
      refresh,
      isLoading,
    }}>
      {children}
    </NotificationsContext.Provider>
  )
}

const EMPTY_NOTIFICATIONS: NotificationsContextValue = {
  notifications: [],
  hasActive: false,
  refresh: () => {},
  isLoading: false,
}

export function useNotifications(): NotificationsContextValue {
  return useContext(NotificationsContext) ?? EMPTY_NOTIFICATIONS
}
